import React, { useState, useRef, useEffect } from 'react';
import { uploadAvatarFile, validateImageFile } from '../lib/contentImages';
import AvatarCropDialog from './AvatarCropDialog';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';

const ImageUpload = ({ value, onChange, label = "Profile Picture" }) => {
    const [preview, setPreview] = useState(value || '');
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState('');
    const [cropSrc, setCropSrc] = useState('');
    const fileInputRef = useRef(null);
    const localPreviewRef = useRef('');

    const revokeLocalPreview = () => {
        if (localPreviewRef.current) {
            URL.revokeObjectURL(localPreviewRef.current);
            localPreviewRef.current = '';
        }
    };

    useEffect(() => {
        if (value && !localPreviewRef.current) {
            setPreview(value);
        }
    }, [value]);

    useEffect(() => () => revokeLocalPreview(), []);

    const closeCropper = () => {
        if (cropSrc) {
            URL.revokeObjectURL(cropSrc);
        }
        setCropSrc('');
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if (!selectedFile) return;

        const validationError = validateImageFile(selectedFile);
        if (validationError) {
            setError(validationError);
            e.target.value = '';
            return;
        }

        setError('');
        if (cropSrc) {
            URL.revokeObjectURL(cropSrc);
        }
        setCropSrc(URL.createObjectURL(selectedFile));
    };

    const handleCropConfirm = async (blob) => {
        const localUrl = URL.createObjectURL(blob);
        const previousPreview = preview;
        setPreview(localUrl);
        setUploading(true);
        try {
            const file = new File([blob], 'avatar.png', { type: blob.type || 'image/png' });
            const { url, key } = await uploadAvatarFile(file);
            revokeLocalPreview();
            localPreviewRef.current = localUrl;
            onChange({ url, key });
            setError('');
            closeCropper();
        } catch (err) {
            URL.revokeObjectURL(localUrl);
            setPreview(previousPreview);
            console.error('Upload error:', err);
            setError(err.message || 'Failed to upload image');
            throw err;
        } finally {
            setUploading(false);
        }
    };

    const handleRemove = () => {
        revokeLocalPreview();
        setPreview('');
        onChange({ url: '', key: '' });
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    return (
        <div>
            <label className="block text-sm font-medium text-text-secondary mb-1">
                {label}
            </label>
            
            {preview && (
                <div className="mb-3 relative inline-block">
                    <Avatar className="h-32 w-32 border border-border">
                        <AvatarImage key={preview} src={preview} alt="Preview" />
                        <AvatarFallback>Photo</AvatarFallback>
                    </Avatar>
                    <button
                        type="button"
                        onClick={handleRemove}
                        className="absolute top-0 right-0 bg-error text-text-on-accent rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-error transition"
                        title="Remove image"
                    >
                        ×
                    </button>
                </div>
            )}

            {error && (
                <p className="text-error text-sm mb-2">{error}</p>
            )}

            <div className="flex gap-2 items-center">
                <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    onChange={handleFileChange}
                    disabled={uploading}
                    className="block w-full text-sm text-text-secondary file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-secondary file:text-accent hover:file:bg-secondary file:cursor-pointer disabled:opacity-50"
                />
                {uploading && (
                    <span className="text-sm text-text-secondary">Uploading...</span>
                )}
            </div>

            <AvatarCropDialog
                open={Boolean(cropSrc)}
                imageSrc={cropSrc}
                label={label}
                onCancel={closeCropper}
                onConfirm={handleCropConfirm}
            />
        </div>
    );
};

export default ImageUpload;
